import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Copy, Loader2, Trash2, UserPlus, UsersRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { TeamAccess, TeamCatalog, TeamInviteResult, TeamMember } from '@/api/types';
import { ROLE_TITLES } from '@/lib/access';
import { BottomSheet } from '@/components/ui/dialog';
import {
  TeamAccessEditor,
  accessIsValid,
  accessSummary,
} from '@/components/TeamAccessEditor';
import { TeamActivity } from '@/components/TeamActivity';
import { getWebApp, haptic } from '@/lib/telegram';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';

export const Route = createFileRoute('/team')({ component: Team });

const DEFAULT_ACCESS: TeamAccess = { role: 'manager' };

/** «Был в сети»: отметка пишется не чаще раза в десять минут, секунды тут были бы враньём. */
function lastSeen(iso?: string | null): string {
  if (!iso) return 'ещё не заходил';
  const at = new Date(iso);
  const ago = Date.now() - at.getTime();
  if (ago < 15 * 60_000) return 'в сети недавно';
  if (at.toDateString() === new Date().toDateString()) {
    return `сегодня в ${at.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return at.toLocaleDateString('ru-RU');
}

/**
 * Кто ещё работает в кабинете и что ему можно.
 *
 * Приглашаем ссылкой или в личку по @нику: человек видит, кто зовёт и на какую роль, и
 * подтверждает кнопкой в боте. Права у каждого свои: готовая роль или набор переключателей.
 */
function Team() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<TeamMember | null>(null);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const teamQ = useQuery({ queryKey: ['team'], queryFn: () => api.team() });
  const catalogQ = useQuery({
    queryKey: ['team-permissions'],
    queryFn: () => api.teamPermissions(),
    staleTime: 10 * 60_000,
  });
  const team = teamQ.data;
  const catalog = catalogQ.data;
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['team'] });

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      haptic('success');
      toast.success('Ссылка скопирована, отправьте её человеку');
    } catch {
      // Буфер в Telegram WebView часто закрыт — показываем ссылку, чтобы скопировать руками.
      toast.info(link);
    }
  };

  const cancelInvite = useMutation({
    mutationFn: (token: string) => api.cancelTeamInvite(token),
    onSuccess: () => {
      toast.success('Приглашение отозвано');
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || 'Не удалось отозвать'),
  });

  const revoke = useMutation({
    mutationFn: (partnerId: string) => api.revokeTeamMember(partnerId),
    onSuccess: () => {
      haptic('success');
      toast.success('Доступ закрыт');
      setConfirmRevoke(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || 'Не удалось снять доступ'),
  });

  const members = team?.members ?? [];
  const used = members.length;
  const limit = team?.limit ?? 0;
  const limitReached = !!team && used >= limit;

  return (
    <div className="p-4 pb-24 space-y-4">
      <h2 className="text-[25px] font-extrabold tracking-[-0.8px] px-2 text-foreground">Команда</h2>

      <div className="surface-card rounded-[22px] p-[18px] space-y-3">
        <p className="text-[12.5px] text-dim leading-snug">
          Люди, которые работают в вашем кабинете. Каждому можно выдать готовую роль или
          отметить разделы вручную. Выводы, ваучеры, удаление ботов и этот раздел остаются
          только у вас.
        </p>
        <button
          type="button"
          disabled={!team || !catalog || limitReached}
          onClick={() => setInviting(true)}
          className="w-full py-3 rounded-[15px] text-sm font-bold bg-white/[0.07] text-foreground disabled:opacity-40 flex items-center justify-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Пригласить
        </button>
        {team && (
          <p className="text-[12px] text-dim">
            {limitReached
              ? `Лимит: ${limit} человек. Снимите кого-то, чтобы позвать нового.`
              : `Занято ${used} из ${limit}. Приглашение действует сутки.`}
          </p>
        )}
      </div>

      {team && team.invites.length > 0 && (
        <div className="surface-card rounded-[22px] p-[18px] space-y-2.5">
          <div className="eyebrow">Ждут ответа</div>
          {team.invites.map((inv) => (
            <div key={inv.token} className="flex items-center gap-2">
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-foreground">
                  {ROLE_TITLES[inv.role ?? 'admin']}
                </span>
                <span className="block truncate font-mono text-[12px] text-dim">{inv.link}</span>
              </span>
              <button
                type="button"
                onClick={() => void copy(inv.link)}
                aria-label="Скопировать ссылку"
                className="relative p-2 after:absolute after:-inset-1 after:content-['']"
              >
                <Copy className="w-4 h-4 text-dim" />
              </button>
              <button
                type="button"
                disabled={cancelInvite.isPending}
                onClick={() => cancelInvite.mutate(inv.token)}
                aria-label="Отозвать приглашение"
                className="relative p-2 disabled:opacity-50 after:absolute after:-inset-1 after:content-['']"
              >
                <Trash2 className="w-4 h-4 text-danger" />
              </button>
            </div>
          ))}
        </div>
      )}

      {teamQ.isPending ? (
        <SkeletonCard className="rounded-[22px] min-h-0 divide-y divide-white/5">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <Skeleton className="w-9 h-9 rounded-[12px] shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <SkeletonLine className="w-1/3" />
                <SkeletonLine size="xs" className="w-24" />
              </div>
              <SkeletonLine size="sm" className="w-10 shrink-0" />
            </div>
          ))}
        </SkeletonCard>
      ) : used === 0 ? (
        <div className="surface-card rounded-[22px] py-10 px-5 text-center">
          <div className="w-[52px] h-[52px] rounded-[16px] bg-white/[0.05] flex items-center justify-center mx-auto mb-3.5 text-[#6b6d75]">
            <UsersRound className="w-[26px] h-[26px]" strokeWidth={1.6} />
          </div>
          <div className="font-display font-semibold text-[15px] text-muted-foreground">
            Пока никого нет
          </div>
          <div className="text-[12.5px] text-dim mt-1.5">
            Нажмите «Пригласить» и выберите, что человеку можно.
          </div>
        </div>
      ) : (
        <div className="surface-card rounded-[22px] divide-y divide-white/5">
          {members.map((m) => (
            <div key={m.partner_id} className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-[12px] bg-white/[0.06] flex items-center justify-center text-sm font-bold shrink-0">
                  {(m.display_name[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate text-foreground">
                    {m.display_name}
                  </div>
                  <div className="text-[12px] text-dim truncate">
                    {m.tg_username ? `@${m.tg_username} · ` : ''}
                    {lastSeen(m.last_seen_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0 text-[12.5px]">
                  <div className="text-foreground">{ROLE_TITLES[m.role ?? 'admin']}</div>
                  <div className="text-dim truncate">{accessSummary(m, catalog)}</div>
                </div>
                <button
                  type="button"
                  disabled={!catalog}
                  onClick={() => setEditing(m)}
                  className="px-3 py-2 rounded-[12px] bg-white/[0.07] text-[13px] font-semibold text-foreground disabled:opacity-40"
                >
                  Доступ
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(m)}
                  className="text-[13px] font-semibold text-danger px-2 py-2"
                >
                  Снять
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {team && <TeamActivity members={members} />}

      {catalog && (
        <InviteSheet
          open={inviting}
          catalog={catalog}
          copy={copy}
          onClose={() => {
            setInviting(false);
            invalidate();
          }}
        />
      )}

      {catalog && editing && (
        <AccessSheet
          member={editing}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}

      <BottomSheet
        open={confirmRevoke !== null}
        onClose={() => setConfirmRevoke(null)}
        title="Снять доступ?"
      >
        <p className="text-sm text-muted-foreground">
          {confirmRevoke?.display_name} потеряет доступ немедленно, даже если у него
          открыт кабинет. Мы отправим ему уведомление.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setConfirmRevoke(null)}
            className="bg-muted text-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
          >
            Отмена
          </button>
          <button
            type="button"
            disabled={revoke.isPending}
            onClick={() => confirmRevoke && revoke.mutate(confirmRevoke.partner_id)}
            className="bg-red-500 text-white py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {revoke.isPending ? 'Снимаем…' : 'Снять'}
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}

/** Права уже добавленного человека. Сохраняется явной кнопкой: это доступ к клиентам. */
function AccessSheet({
  member,
  catalog,
  onClose,
  onSaved,
}: {
  member: TeamMember;
  catalog: TeamCatalog;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial: TeamAccess = {
    role: member.role ?? 'admin',
    ...(member.role === 'custom' ? { permissions: member.permissions ?? [] } : {}),
  };
  const [value, setValue] = useState<TeamAccess>(initial);

  const save = useMutation({
    mutationFn: () => api.setTeamAccess(member.partner_id, value),
    onSuccess: () => {
      haptic('success');
      toast.success('Права сохранены');
      onSaved();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message || 'Не удалось сохранить права');
    },
  });

  const changed = JSON.stringify(value) !== JSON.stringify(initial);

  return (
    <BottomSheet open onClose={onClose} title={member.display_name}>
      <TeamAccessEditor value={value} onChange={setValue} catalog={catalog} disabled={save.isPending} />
      {!accessIsValid(value) && (
        <p className="text-[12.5px] text-danger">Отметьте хотя бы один раздел.</p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onClose}
          className="bg-muted text-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
        >
          Отмена
        </button>
        <button
          type="button"
          disabled={save.isPending || !changed || !accessIsValid(value)}
          onClick={() => save.mutate()}
          className="bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Сохранить
        </button>
      </div>
    </BottomSheet>
  );
}

/** Новое приглашение: роль и права, получатель по желанию, потом готовая ссылка. */
function InviteSheet({
  open,
  catalog,
  copy,
  onClose,
}: {
  open: boolean;
  catalog: TeamCatalog;
  copy: (link: string) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState<TeamAccess>(DEFAULT_ACCESS);
  const [handle, setHandle] = useState('');
  const [result, setResult] = useState<TeamInviteResult | null>(null);

  const close = () => {
    setValue(DEFAULT_ACCESS);
    setHandle('');
    setResult(null);
    onClose();
  };

  const create = useMutation({
    mutationFn: () => api.createTeamInvite({ ...value, handle: handle.trim() || null }),
    onSuccess: (r) => {
      haptic('success');
      setResult(r);
    },
    onError: (e: Error) => {
      haptic('error');
      // Причину показываем как есть: глухое «не удалось» не отличает лимит от обрыва сети.
      toast.error(e.message || 'Не удалось создать приглашение');
    },
  });

  return (
    <BottomSheet open={open} onClose={close} title="Пригласить в команду">
      {result ? (
        <>
          <p className="text-sm text-foreground">
            {result.delivered === true
              ? 'Отправили приглашение в личку. Человек увидит, кто зовёт и на какую роль, и подтвердит кнопкой.'
              : result.delivered === false
                ? (result.delivery_note ?? 'В личку не дошло. Отправьте ссылку сами.')
                : 'Приглашение готово. Отправьте человеку ссылку сами.'}
          </p>
          <div className="surface-inset rounded-[15px] p-3 break-all font-mono text-[12px] text-foreground select-all">
            {result.link}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => void copy(result.link)}
              className="bg-muted text-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
            >
              Скопировать
            </button>
            <button
              type="button"
              onClick={close}
              className="bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
            >
              Готово
            </button>
          </div>
        </>
      ) : (
        <>
          <TeamAccessEditor
            value={value}
            onChange={setValue}
            catalog={catalog}
            disabled={create.isPending}
          />
          <label className="block space-y-1.5">
            <span className="eyebrow block">Кому (по желанию)</span>
            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@username"
              autoComplete="off"
              autoCapitalize="none"
              maxLength={64}
              disabled={create.isPending}
              className="surface-inset w-full rounded-[15px] px-4 py-3 text-[16px] text-foreground outline-none placeholder:text-dim disabled:opacity-50"
            />
            <span className="block text-[12px] text-dim leading-snug">
              @ник или Telegram ID. Если человек уже открывал нашего бота, приглашение придёт
              ему в личку. Иначе получите ссылку.
            </span>
          </label>
          {!accessIsValid(value) && (
            <p className="text-[12.5px] text-danger">Отметьте хотя бы один раздел.</p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={close}
              className="bg-muted text-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform"
            >
              Отмена
            </button>
            <button
              type="button"
              disabled={create.isPending || !accessIsValid(value)}
              onClick={() => create.mutate()}
              className="bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {create.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Создать
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}
